import { ChevronDownIcon, ChevronUpIcon, Columns3Icon, GripVerticalIcon } from "lucide-react";
import { Reorder, useDragControls } from "motion/react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { call, type components, intentDone } from "../lib/api";
import { useT } from "../shell/i18n";
import { ProblemNotice } from "../shell/problem";
import { durationOf, easeOf } from "./motion-runtime";
import {
  columnSegments,
  effectiveColumnOrder,
  effectiveColumns,
  type PresentableColumn,
  reorderColumnSegment,
} from "./table-presentation";
import type { TableSurfaceId } from "./table-surfaces";

type Preference = components["schemas"]["TablePersonalization"];

interface TablePersonalizationStore {
  readonly preference: (
    surfaceId: TableSurfaceId,
    schemaRevision: number,
  ) => Preference | undefined;
  readonly save: (
    surfaceId: TableSurfaceId,
    schemaRevision: number,
    visibleColumns: readonly string[],
    columnOrder: readonly string[],
  ) => Promise<void>;
  readonly reset: (surfaceId: TableSurfaceId, schemaRevision: number) => Promise<void>;
}

const TablePersonalizationContext = createContext<TablePersonalizationStore | null>(null);

function keyOf(surfaceId: string, schemaRevision: number) {
  return `${surfaceId}@${schemaRevision}`;
}

export function TablePersonalizationProvider({
  initial,
  children,
}: {
  readonly initial: readonly Preference[];
  readonly children: ReactNode;
}) {
  const [preferences, setPreferences] = useState<ReadonlyMap<string, Preference>>(
    () =>
      new Map(
        initial.map((preference) => [
          keyOf(preference.surface_id, preference.schema_revision),
          preference,
        ]),
      ),
  );

  const preference = useCallback(
    (surfaceId: TableSurfaceId, schemaRevision: number) =>
      preferences.get(keyOf(surfaceId, schemaRevision)),
    [preferences],
  );

  const save = useCallback(
    async (
      surfaceId: TableSurfaceId,
      schemaRevision: number,
      visibleColumns: readonly string[],
      columnOrder: readonly string[],
    ) => {
      const at = keyOf(surfaceId, schemaRevision);
      const current = preferences.get(at);
      const intent = `table-personalization:${at}`;
      const answer = await call<Preference>(
        `/v1/ui/table-personalizations/${encodeURIComponent(surfaceId)}`,
        {
          method: "POST",
          intent,
          body: {
            schema_revision: schemaRevision,
            visible_columns: visibleColumns,
            column_order: columnOrder,
            expected_belief_id: current?.belief_id ?? null,
          },
        },
      );
      intentDone(intent);
      setPreferences((held) => new Map(held).set(at, answer));
    },
    [preferences],
  );

  const reset = useCallback(
    async (surfaceId: TableSurfaceId, schemaRevision: number) => {
      const at = keyOf(surfaceId, schemaRevision);
      const current = preferences.get(at);
      if (!current) return;
      const intent = `table-personalization-reset:${at}`;
      await call(`/v1/ui/table-personalizations/${encodeURIComponent(surfaceId)}/resets`, {
        method: "POST",
        intent,
        body: { schema_revision: schemaRevision, expected_belief_id: current.belief_id },
      });
      intentDone(intent);
      setPreferences((held) => {
        const next = new Map(held);
        next.delete(at);
        return next;
      });
    },
    [preferences],
  );

  const value = useMemo(() => ({ preference, save, reset }), [preference, save, reset]);
  return (
    <TablePersonalizationContext.Provider value={value}>
      {children}
    </TablePersonalizationContext.Provider>
  );
}

export function useTablePersonalization() {
  return useContext(TablePersonalizationContext);
}

export function TableColumnCustomizer({
  columns,
  surfaceId,
  schemaRevision,
  kind = "table",
}: {
  readonly columns: readonly (PresentableColumn & { readonly header: string })[];
  readonly surfaceId: TableSurfaceId;
  readonly schemaRevision: number;
  readonly kind?: "table" | "list";
}) {
  const t = useT();
  const store = useTablePersonalization();
  const stored = store?.preference(surfaceId, schemaRevision);

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button variant="outline" size="sm">
            <Columns3Icon className="size-4" aria-hidden="true" />
            {t(kind === "table" ? "table.columns" : "table.fields")}
          </Button>
        }
      />
      <PopoverContent
        align="end"
        className="max-h-(--available-height) max-w-(--available-width) w-80 overflow-hidden"
      >
        <div className="mb-3 shrink-0">
          <h3 className="m-0 text-shell-body font-medium">
            {t(kind === "table" ? "table.columnsHeading" : "table.fieldsHeading")}
          </h3>
          <p className="m-0 mt-1 text-shell-caption text-muted-foreground">
            {t(kind === "table" ? "table.columnsHint" : "table.fieldsHint")}
          </p>
        </div>
        <TableColumnEditor
          key={stored?.belief_id ?? `${surfaceId}-product-default`}
          columns={columns}
          surfaceId={surfaceId}
          schemaRevision={schemaRevision}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The shared table adapter used by both the quick Columns popover and page editing mode. */
export function TableColumnEditor({
  columns,
  surfaceId,
  schemaRevision,
}: {
  readonly columns: readonly (PresentableColumn & { readonly header: string })[];
  readonly surfaceId: TableSurfaceId;
  readonly schemaRevision: number;
}) {
  const t = useT();
  const store = useTablePersonalization();
  const stored = store?.preference(surfaceId, schemaRevision);
  const ordered = effectiveColumnOrder(columns, stored).map((column) => column.key);
  const shown = effectiveColumns(columns, schemaRevision, stored).map((column) => column.key);
  const [order, setOrder] = useState<readonly string[]>(ordered);
  const [visible, setVisible] = useState<ReadonlySet<string>>(new Set(shown));
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  function move(key: string, by: -1 | 1) {
    setOrder((current) => {
      const from = current.indexOf(key);
      const to = from + by;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const moving = columns.find((column) => column.key === key);
      const crossing = columns.find((column) => column.key === current[to]);
      if (
        !moving ||
        !crossing ||
        moving.presentation === "required" ||
        moving.presentation === "roleLocked" ||
        crossing.presentation === "required" ||
        crossing.presentation === "roleLocked"
      ) {
        return current;
      }
      const next = [...current];
      [next[from], next[to]] = [next[to] as string, next[from] as string];
      return next;
    });
  }

  function toggle(key: string, on: boolean) {
    setVisible((current) => {
      const next = new Set(current);
      if (on) next.add(key);
      else if (next.size > 1) next.delete(key);
      return next;
    });
  }

  async function save() {
    if (!store || sending) return;
    setSending(true);
    setFailure(null);
    try {
      await store.save(
        surfaceId,
        schemaRevision,
        order.filter((key) => visible.has(key)),
        order,
      );
    } catch (problem) {
      setFailure(problem);
    } finally {
      setSending(false);
    }
  }

  async function reset() {
    if (!store || !stored || sending) return;
    setSending(true);
    setFailure(null);
    try {
      await store.reset(surfaceId, schemaRevision);
    } catch (problem) {
      setFailure(problem);
    } finally {
      setSending(false);
    }
  }

  if (!store) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {failure ? <ProblemNotice failure={failure} /> : null}
      <div className="flex min-h-0 flex-col gap-1 overflow-y-auto overscroll-contain">
        {columnSegments(columns, order).map((segment) => {
          const rows = segment.keys.map((key) => {
            const index = order.indexOf(key);
            const column = columns.find((candidate) => candidate.key === key);
            if (!column) return null;
            const locked =
              column.presentation === "required" || column.presentation === "roleLocked";
            const before =
              index > 0 ? columns.find((candidate) => candidate.key === order[index - 1]) : null;
            const after =
              index < order.length - 1
                ? columns.find((candidate) => candidate.key === order[index + 1])
                : null;
            const cannotMoveUp =
              locked ||
              index === 0 ||
              before?.presentation === "required" ||
              before?.presentation === "roleLocked";
            const cannotMoveDown =
              locked ||
              index === order.length - 1 ||
              after?.presentation === "required" ||
              after?.presentation === "roleLocked";
            return (
              <ColumnEditorRow
                key={key}
                columnKey={key}
                header={column.header}
                draggable={!locked && segment.keys.length > 1 && !sending}
                reorderable={!locked}
              >
                <Checkbox
                  checked={visible.has(key)}
                  disabled={sending || locked || (visible.has(key) && visible.size === 1)}
                  aria-label={column.header}
                  onCheckedChange={(next) => toggle(key, next === true)}
                />
                <span className="min-w-0 flex-1 truncate text-shell-body">{column.header}</span>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  disabled={sending || cannotMoveUp}
                  aria-label={t("table.moveColumnUp", { column: column.header })}
                  onClick={() => move(key, -1)}
                >
                  <ChevronUpIcon aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  disabled={sending || cannotMoveDown}
                  aria-label={t("table.moveColumnDown", { column: column.header })}
                  onClick={() => move(key, 1)}
                >
                  <ChevronDownIcon aria-hidden="true" />
                </Button>
              </ColumnEditorRow>
            );
          });
          const key = [...segment.keys].sort().join("|");
          return segment.locked ? (
            <ul key={key} className="m-0 flex list-none flex-col gap-1 p-0">
              {rows}
            </ul>
          ) : (
            <Reorder.Group
              key={key}
              axis="y"
              values={[...segment.keys]}
              onReorder={(next) => {
                if (!sending)
                  setOrder((current) => reorderColumnSegment(current, segment.keys, next));
              }}
              className="m-0 flex list-none flex-col gap-1 p-0"
            >
              {rows}
            </Reorder.Group>
          );
        })}
      </div>
      <div className="flex shrink-0 items-center justify-between border-line-subtle border-t pt-3">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={!stored || sending}
          onClick={reset}
        >
          {t("table.resetColumns")}
        </Button>
        <Button type="button" size="sm" disabled={sending} onClick={save}>
          {t("table.saveColumns")}
        </Button>
      </div>
    </div>
  );
}

function ColumnEditorRow({
  columnKey,
  header,
  draggable,
  reorderable,
  children,
}: {
  readonly columnKey: string;
  readonly header: string;
  readonly draggable: boolean;
  readonly reorderable: boolean;
  readonly children: ReactNode;
}) {
  const t = useT();
  const controls = useDragControls();
  const content = (
    <>
      {reorderable ? (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="touch-none cursor-grab active:cursor-grabbing"
          aria-label={t("table.dragColumn", { column: header })}
          disabled={!draggable}
          onPointerDown={(event) => {
            if (draggable) controls.start(event);
          }}
        >
          <GripVerticalIcon aria-hidden="true" />
        </Button>
      ) : null}
      {children}
    </>
  );
  return reorderable ? (
    <Reorder.Item
      value={columnKey}
      dragListener={false}
      dragControls={controls}
      dragMomentum={false}
      transition={{
        type: "tween",
        duration: durationOf("--motion-duration-row-enter") / 1000,
        ease: easeOf("--motion-ease-move", [0, 0, 1, 1]),
      }}
      className="relative flex min-h-9 items-center gap-2 bg-surface-panel"
    >
      {content}
    </Reorder.Item>
  ) : (
    <li className="flex min-h-9 items-center gap-2">{content}</li>
  );
}
